// @flow strict
/* eslint-disable ta-code-conventions/has-matching-test */
import * as React from 'react';
import classnames from '@ta/common.classnames';
import useTransition from '@ta/common.transitions';
import { OverlayPortal } from '@ta/lithium.portals';
import styles from './ToastProvider.css';

type CloseToast = () => mixed;
type ToastPayload = {|
  /** The toast item */
  node: React.Node,
  /** how long the toast should be displayed. Defaults to 4 seconds  */
  timeout?: number,
|};

/** A callback to launch a toast and returns a promise that eventually resolves to three states
 * - dismissed: The user manually prompted to exit the toast
 * - timeout: The toast was removed naturally and timed out
 * - collision: The toast was removed because another toast was subsequently added
 * */
type LaunchToast = (toast: ToastPayload) => Promise<'dismissed' | 'timeout' | 'collision'>;

type ToastContext = [LaunchToast, CloseToast];

/**
 * Exposes an imperative API to launch toasts via a context
 */
export const Toast = React.createContext<ToastContext>([
  () =>
    new Promise(() => {
      console.error('Toast provider was not set!');
    }),
  () => {
    console.error('Toast provider was not set!');
  },
]);

type ProviderProps = {|
  children: React.Node,
|};

type ConsumerProps = {|
  /** Render prop to open and close the toast */
  children(LaunchToast, CloseToast): React.Node,
|};

const classNames = {
  enter: styles.fadeInEnter,
  entering: styles.fadeInEnterActive,
  entered: styles.fadeInEnterDone,
  exit: styles.fadeOutExit,
  exiting: styles.fadeOutExitActive,
  exited: styles.fadeOutExitDone,
};

const ToastProvider = ({ children }: ProviderProps) => {
  // The current toast to render
  const [activeToast, setActiveToast] = React.useState(null);
  // The toast scheduled and to eventually render. Includes a counter to drive the effect on each change
  const [[scheduledToast, counter], setScheduledToast] = React.useState([undefined, 0]);
  // State used to dictate whether toast is currently visible, and the bit value useTransition is looking for
  const [show, setShow] = React.useState(false);
  // Keep track of the pending promise within a ref, so they can be resolved/rejected outside its declared lexical scope
  const ongoingPromise = React.useRef(null);

  const status = useTransition({ in: show, timeout: 300, forceDocumentReflow: true });

  const closeToast = React.useCallback(() => {
    setShow(false);
    if (ongoingPromise.current) {
      const [prevResolver, prevCleanup] = ongoingPromise.current;
      prevResolver('dismissed');
      prevCleanup();
    }
  }, []);

  const launchToast = React.useCallback(
    ({ node, timeout = 4000 }) =>
      new Promise(resolve => {
        if (ongoingPromise.current) {
          const [prevResolver, prevCleanup] = ongoingPromise.current;
          prevResolver('collision');
          prevCleanup();
        }
        // Launching a new toast should always reset visibility to false and then schedule a toast in the future.
        // That way, launchToast implies show goes from false -> true, we can get that sleek animation when multiple toasts are launched within a short time span
        setShow(false);
        setScheduledToast(([, prevCounter]) => [node, prevCounter + 1]);
        const timeoutId = setTimeout(() => {
          setShow(false);
          resolve('timeout');
          ongoingPromise.current = null;
        }, timeout);

        const cleanup = () => {
          clearTimeout(timeoutId);
          ongoingPromise.current = null;
        };
        ongoingPromise.current = [resolve, cleanup];
      }),
    [],
  );

  const context = React.useMemo(() => [launchToast, closeToast], [launchToast, closeToast]);
  React.useEffect(() => {
    if (counter !== 0) {
      setShow(true);
      setActiveToast(scheduledToast);
    }
  }, [scheduledToast, counter]);

  return (
    <Toast.Provider value={context}>
      {children}
      {status !== 'exited' && (
        <OverlayPortal className={classnames(styles.toast, classNames[status])}>
          {activeToast}
        </OverlayPortal>
      )}
    </Toast.Provider>
  );
};

/**
 * Wrapper around Toast.Consumer
 */
export const ToastConsumer = ({ children }: ConsumerProps) => {
  const [launchToast, closeToast] = React.useContext(Toast);
  return children(launchToast, closeToast);
};

export default ToastProvider;
